/**
 * Mapbox GL Initialization and Configuration
 * Handles satellite base layer and D3 integration
 */

import mapboxgl from 'mapbox-gl';

// Initial view shown before race data loads. The map then auto-frames the
// fleet via fitToTeams(), so this is just a neutral starting point that works
// for any yb.tl race rather than a course-specific bounding box.
const DEFAULT_VIEW = {
  center: { lat: 20, lng: 0 },
  zoom: 1.5,
};

export class MapController {
  constructor(containerId, token) {
    this.container = document.getElementById(containerId);
    this.map = null;
    this.d3Overlay = null;
    this.callbacks = {
      move: new Set(),
      zoom: new Set(),
      click: new Set(),
    };

    this.initMap(token);
  }

  initMap(token) {
    mapboxgl.accessToken = token;

    this.map = new mapboxgl.Map({
      container: this.container,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [DEFAULT_VIEW.center.lng, DEFAULT_VIEW.center.lat],
      zoom: DEFAULT_VIEW.zoom,
      minZoom: 2,
      maxZoom: 12,
      pitch: 0,
      bearing: 0,
      projection: 'mercator',
      attributionControl: true,
      logoPosition: 'bottom-right',
    });

    // Add navigation controls
    this.map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: false }),
      'bottom-right'
    );

    // Add scale
    this.map.addControl(
      new mapboxgl.ScaleControl({ unit: 'nautical' }),
      'bottom-left'
    );

    // Setup event listeners
    this.map.on('move', () => this.notifyMove());
    this.map.on('zoom', () => this.notifyZoom());
    this.map.on('click', (e) => this.notifyClick(e));

    // Add ocean depth styling when loaded
    this.map.on('load', () => this.addOceanLayers());
  }

  /**
   * Add custom ocean depth and atmosphere layers
   */
  addOceanLayers() {
    // Add subtle ocean depth gradient overlay
    this.map.addLayer({
      id: 'ocean-depth',
      type: 'background',
      paint: {
        'background-color': 'rgba(5, 15, 35, 0.3)',
      },
    }, 'satellite');

    // Darken deep ocean areas
    //this.map.setPaintProperty('water', 'fill-color', [
     // 'interpolate',
    //  ['linear'],
    //  ['zoom'],
    //  0, 'rgba(8, 20, 40, 0.4)',
    //  5, 'rgba(10, 25, 50, 0.3)',
    //  10, 'rgba(15, 35, 60, 0.2)',
    //]);
  }

  /**
   * Project lat/lng to pixel coordinates
   */
  project(lng, lat) {
    const point = this.map.project([lng, lat]);
    return [point.x, point.y];
  }

  /**
   * Unproject pixel coordinates to lat/lng
   */
  unproject(x, y) {
    const lngLat = this.map.unproject([x, y]);
    return [lngLat.lng, lngLat.lat];
  }

  /**
   * Get current map bounds
   */
  getBounds() {
    return this.map.getBounds();
  }

  /**
   * Fly to a specific location
   */
  flyTo(lng, lat, zoom = null) {
    this.map.flyTo({
      center: [lng, lat],
      zoom: zoom || this.map.getZoom(),
      duration: 1500,
      essential: true,
    });
  }

  /**
   * Fit bounds to show all teams
   */
  fitToTeams(teams) {
    if (!teams || teams.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();

    // Frame the whole course from the data: extend to every tracked position
    // so the full fleet and its trails are visible for any race.
    teams.forEach(team => {
      (team.positions || []).forEach(pos => bounds.extend([pos.lng, pos.lat]));
      if (team.latest) {
        bounds.extend([team.latest.lng, team.latest.lat]);
      }
    });

    if (bounds.isEmpty()) return;

    this.map.fitBounds(bounds, {
      padding: { top: 100, bottom: 150, left: 350, right: 350 },
      duration: 1500,
    });
  }

  /**
   * Reset view to default
   */
  resetView() {
    this.map.flyTo({
      center: [DEFAULT_VIEW.center.lng, DEFAULT_VIEW.center.lat],
      zoom: DEFAULT_VIEW.zoom,
      duration: 1500,
    });
  }

  /**
   * Subscribe to map move events
   */
  onMove(callback) {
    this.callbacks.move.add(callback);
    return () => this.callbacks.move.delete(callback);
  }

  /**
   * Subscribe to map zoom events
   */
  onZoom(callback) {
    this.callbacks.zoom.add(callback);
    return () => this.callbacks.zoom.delete(callback);
  }

  /**
   * Subscribe to map click events
   */
  onClick(callback) {
    this.callbacks.click.add(callback);
    return () => this.callbacks.click.delete(callback);
  }

  notifyMove() {
    this.callbacks.move.forEach(cb => cb());
  }

  notifyZoom() {
    this.callbacks.zoom.forEach(cb => cb());
  }

  notifyClick(e) {
    this.callbacks.click.forEach(cb => cb(e));
  }

  /**
   * Get underlying mapbox instance
   */
  getMap() {
    return this.map;
  }

  /**
   * Check if map is loaded
   */
  isLoaded() {
    return this.map.loaded();
  }

  /**
   * Wait for map to load
   */
  async whenReady() {
    return new Promise(resolve => {
      if (this.map.loaded()) {
        resolve();
      } else {
        this.map.on('load', resolve);
      }
    });
  }

  /**
   * Get canvas dimensions
   */
  getCanvasSize() {
    const canvas = this.map.getCanvas();
    return {
      width: canvas.width,
      height: canvas.height,
    };
  }

  /**
   * Destroy map instance
   */
  destroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}
